import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "../lib/api";

export default function Article() {
  const { id } = useParams();
  const [art, setArt] = useState(null);
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    const [a, c] = await Promise.all([
      http.get(`/articles/${id}`),
      http.get(`/articles/${id}/comments`),
    ]);
    setArt(a.data);
    setComments(c.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      await http.post(`/articles/${id}/comments`, { name: name.trim() || "Anónimo", text: text.trim() });
      setText("");
      const { data } = await http.get(`/articles/${id}/comments`);
      setComments(data);
    } finally {
      setPosting(false);
    }
  };

  if (!art) {
    return <div className="max-w-3xl mx-auto px-6 py-20 font-mono text-sm text-sub">Cargando…</div>;
  }

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-8 py-10 animate-fade-in" data-testid="article-page">
      <Link to={`/categoria/${art.category}`} className="font-mono text-[11px] uppercase tracking-widest text-sub hover:text-rojo">
        ← {art.category}
      </Link>
      <div className="mt-4 flex items-center gap-2 text-sub">
        <span className="source-badge">{art.source_code}</span>
        <span className="font-mono text-[12px] uppercase tracking-widest">{art.source_name}</span>
      </div>
      <h1 className="font-serif-display tracking-news text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mt-3" data-testid="article-title">
        {art.title}
      </h1>
      {art.summary && (
        <p className="mt-5 text-xl text-sub leading-relaxed font-sans">{art.summary}</p>
      )}
      {art.image_url && (
        <div className="mt-6 border border-ink">
          <img src={art.image_url} alt={art.title} className="w-full h-auto" />
        </div>
      )}
      {art.body && (
        <div className="mt-6 text-base leading-relaxed font-sans text-ink whitespace-pre-line">
          {art.body}
        </div>
      )}
      <p className="mt-6 font-mono text-[12px] text-sub">
        Publicado: {new Date(art.published_at).toLocaleString("es-MX")}
      </p>
      <a
        href={art.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-4 border border-ink px-4 py-2 font-mono text-[12px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
        data-testid="article-source-link"
      >
        Leer en {art.source_name} ↗
      </a>

      {/* Comments */}
      <section className="mt-14 dbl-rule pt-6" data-testid="comments-section">
        <h2 className="font-serif-display text-3xl text-ink">Comentarios</h2>
        <p className="text-sm text-sub mt-1">{comments.length} aportaciones</p>

        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="comment-form">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre (opcional)"
            className="w-full bg-transparent border border-ink/30 focus:border-ink outline-none px-3 py-2 font-sans text-sm rounded-none"
            data-testid="comment-name"
            maxLength={60}
          />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Comparte tu análisis…"
            rows={4}
            className="w-full bg-transparent border border-ink/30 focus:border-ink outline-none px-3 py-2 font-sans text-sm rounded-none"
            data-testid="comment-text"
            maxLength={1000}
            required
          />
          <button
            type="submit"
            disabled={posting || !text.trim()}
            className="border border-ink px-5 py-2 font-mono text-[12px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors disabled:opacity-40"
            data-testid="comment-submit"
          >
            {posting ? "Publicando…" : "Publicar comentario"}
          </button>
        </form>

        <ul className="mt-8 space-y-5">
          {comments.map((c) => (
            <li key={c.id} className="border-b border-rule pb-4" data-testid={`comment-${c.id}`}>
              <div className="flex items-baseline justify-between">
                <span className="font-sans font-semibold text-ink">{c.name}</span>
                <span className="font-mono text-[11px] uppercase tracking-widest text-sub">
                  {new Date(c.created_at).toLocaleString("es-MX")}
                </span>
              </div>
              <p className="mt-1 text-[15px] text-ink leading-relaxed whitespace-pre-line">{c.text}</p>
            </li>
          ))}
          {!comments.length && (
            <li className="text-sm text-sub">Sé el primero en comentar.</li>
          )}
        </ul>
      </section>
    </article>
  );
}
